import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, 
  Wrench, 
  Car, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Camera,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  reception: { label: 'En Recepción', color: 'bg-blue-500', icon: Clock, step: 1 },
  in_progress: { label: 'En Progreso', color: 'bg-amber-500', icon: Wrench, step: 2 },
  completed: { label: 'Completado', color: 'bg-emerald-500', icon: CheckCircle, step: 3 },
  delivered: { label: 'Entregado', color: 'bg-slate-500', icon: CheckCircle, step: 4 }
};

const severityColors = {
  low: 'bg-slate-100 border-slate-300 text-slate-700',
  medium: 'bg-amber-50 border-amber-300 text-amber-800',
  high: 'bg-red-50 border-red-300 text-red-800'
};

export default function TrackingPage() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [service, setService] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    
    setLoading(true);
    setError('');
    setService(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/track/${code.trim().toUpperCase()}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Código no encontrado. Verifica que esté correcto.');
        }
        throw new Error('Error al buscar el servicio');
      }
      
      const data = await response.json();
      setService(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const StatusIcon = service ? statusConfig[service.status]?.icon || Clock : Clock;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-[#1E3A5F] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Wrench className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold">Consulta tu Servicio</h1>
          </div>
          <p className="text-white/80 text-sm">
            Ingresa el código de seguimiento que te proporcionó el taller
          </p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Código de Seguimiento
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Ej: TMP-A3X7K2"
                  className="text-lg font-mono tracking-wider h-12 uppercase"
                  maxLength={10}
                  data-testid="tracking-code-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading || !code.trim()}
                className="h-12 px-6 bg-[#1E3A5F] hover:bg-[#162e4d]"
                data-testid="search-tracking-btn"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Buscar
                  </>
                )}
              </Button>
            </div>
            {error && (
              <p className="mt-3 text-red-600 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {error}
              </p>
            )}
          </div>
        </form>

        {/* Service Details */}
        {service && (
          <div className="space-y-6 animate-fade-in" data-testid="tracking-result">
            {/* Workshop Name */}
            {service.workshop_name && (
              <div className="text-center">
                <p className="text-slate-500 text-sm">Taller</p>
                <p className="text-xl font-bold text-slate-900">{service.workshop_name}</p>
              </div>
            )}

            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-full ${statusConfig[service.status]?.color} flex items-center justify-center`}>
                  <StatusIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Estado actual</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {statusConfig[service.status]?.label || service.status}
                  </p>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6">
                {Object.entries(statusConfig).map(([key, config], index) => {
                  const currentStep = statusConfig[service.status]?.step || 1;
                  const isCompleted = config.step < currentStep;
                  const isCurrent = config.step === currentStep;
                  
                  return (
                    <div key={key} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                        ${isCompleted ? 'bg-emerald-500 text-white' : 
                          isCurrent ? `${config.color} text-white` : 
                          'bg-slate-200 text-slate-500'}`}>
                        {isCompleted ? <CheckCircle className="w-4 h-4" /> : config.step}
                      </div>
                      {index < 3 && (
                        <div className={`w-12 md:w-20 h-1 mx-1 ${
                          isCompleted ? 'bg-emerald-500' : 'bg-slate-200'
                        }`} />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="text-center text-xs text-slate-500 flex justify-between px-2">
                <span>Recepción</span>
                <span>Progreso</span>
                <span>Completo</span>
                <span>Entregado</span>
              </div>
            </div>

            {/* Vehicle & Service Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-slate-500" />
                Información del Vehículo
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Vehículo</p>
                  <p className="font-medium text-slate-900">
                    {service.vehicle_brand} {service.vehicle_model}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Año</p>
                  <p className="font-medium text-slate-900">{service.vehicle_year}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Placas</p>
                  <p className="font-mono font-medium text-slate-900">{service.vehicle_plates}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Código</p>
                  <p className="font-mono font-medium text-[#1E3A5F]">{service.tracking_code}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">Servicio</p>
                <p className="font-medium text-slate-900">{service.description}</p>
              </div>

              <div className="mt-4 text-xs text-slate-400">
                Última actualización: {format(new Date(service.updated_at), "d 'de' MMMM 'a las' HH:mm", { locale: es })}
              </div>
            </div>

            {/* Findings */}
            {service.findings && service.findings.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Hallazgos Adicionales
                </h3>
                
                <div className="space-y-3">
                  {service.findings.map((finding, i) => (
                    <div 
                      key={i} 
                      className={`p-3 rounded-lg border ${severityColors[finding.severity] || severityColors.medium}`}
                    >
                      <p className="font-medium">{finding.description}</p>
                      {finding.estimated_cost && (
                        <p className="text-sm mt-1">
                          Costo estimado: ${finding.estimated_cost.toLocaleString('es-MX')} MXN
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Images */}
            {(service.images_before?.length > 0 || service.images_during?.length > 0 || service.images_after?.length > 0) && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-slate-500" />
                  Fotos del Servicio
                </h3>
                
                {['before', 'during', 'after'].map((type) => {
                  const images = service[`images_${type}`] || [];
                  if (images.length === 0) return null;
                  
                  const labels = { before: 'Antes', during: 'Durante', after: 'Después' };
                  
                  return (
                    <div key={type} className="mb-4 last:mb-0">
                      <p className="text-sm font-medium text-slate-600 mb-2">{labels[type]}</p>
                      <div className="grid grid-cols-3 gap-2">
                        {images.map((url, i) => (
                          <a 
                            key={i} 
                            href={`${API_URL}${url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="aspect-square rounded-lg overflow-hidden bg-slate-100"
                          >
                            <img 
                              src={`${API_URL}${url}`} 
                              alt={`${labels[type]} ${i + 1}`} 
                              className="w-full h-full object-cover hover:scale-105 transition-transform"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Refresh Button */}
            <div className="text-center">
              <Button 
                variant="outline" 
                onClick={handleSearch}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Actualizar Estado
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!service && !error && !loading && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500">
              Ingresa tu código de seguimiento para ver el estado de tu servicio
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-sm text-slate-400">
        Powered by TallerMec Pro
      </footer>
    </div>
  );
}
